// src/components/admin/DeleteClassTab.js
import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  deleteDoc,
  doc as fsDoc
} from 'firebase/firestore';
import {
  ref as rtRef,
  remove as rtRemove
} from 'firebase/database';
import {
  ref as storageRef,
  listAll,
  deleteObject
} from 'firebase/storage';
import { db, database, storage } from '../../../firebase';

const DeleteClassTab = () => {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [logs, setLogs] = useState([]);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchClasses = async () => {
      const snap = await getDocs(collection(db, 'classes'));
      setClasses(
        snap.docs.map(d => ({ id: d.id, ...d.data() }))
      );
    };
    fetchClasses();
  }, []);

  const log = msg => setLogs(l => [...l, msg]);

  // recursive deletion for storage folders
  const deleteStorageFolder = async folderRef => {
    const { items, prefixes } = await listAll(folderRef);

    // delete all files in this folder
    await Promise.all(
      items.map(itemRef =>
        deleteObject(itemRef)
          .then(() => log(`Deleted Storage file: ${itemRef.fullPath}`))
          .catch(e => log(`Error deleting ${itemRef.fullPath}: ${e.message}`))
      )
    );

    // recurse into subfolders
    await Promise.all(
      prefixes.map(subRef => deleteStorageFolder(subRef))
    );
  };

  const handleDelete = async () => {
    if (!selectedClass) return;
    setDeleting(true);
    setLogs([]);

    const classId = selectedClass;

    // 1. Realtime clean-up
    for (const path of ['sessions', 'attendance', 'answers']) {
      try {
        await rtRemove(rtRef(database, `${path}/${classId}`));
        log(`Deleted RT path: ${path}/${classId}`);
      } catch (e) {
        log(`Error deleting RT ${path}/${classId}: ${e.message}`);
      }
    }

    // 2. Firestore: delete AIReport/sessionCache sub-docs
    try {
      const cacheSnap = await getDocs(
        collection(db, 'AIReport', classId, 'sessionCache')
      );
      for (const d of cacheSnap.docs) {
        await deleteDoc(
          fsDoc(db, 'AIReport', classId, 'sessionCache', d.id)
        );
        log(`Deleted FS sub-doc: AIReport/${classId}/sessionCache/${d.id}`);
      }
    } catch (e) {
      log(`Error deleting FS sessionCache: ${e.message}`);
    }

    // 3. Firestore: delete top-level docs
    for (const col of [
      'AIReport',
      'classes',
      'enrollments',
      'grades',
      'tickets'
    ]) {
      try {
        await deleteDoc(fsDoc(db, col, classId));
        log(`Deleted FS doc: ${col}/${classId}`);
      } catch (e) {
        log(`Error deleting FS ${col}/${classId}: ${e.message}`);
      }
    }

    // 4. Storage cleanup under questions/{classId}
    try {
      const qRef = storageRef(storage, `questions/${classId}`);
      await deleteStorageFolder(qRef);
      log(`Finished Storage cleanup under questions/${classId}`);
    } catch (e) {
      log(`Error deleting Storage for questions/${classId}: ${e.message}`);
    }

    setDeleting(false);
  };

  return (
    <div>
      <label className="block mb-2 text-[var(--text-color)]">
        Select Class
      </label>

      <select
        className="w-full p-2 mb-4 border border-[var(--muted-color)] rounded bg-[var(--card-bg)] text-[var(--text-color)]"
        style={{
          backgroundColor: 'var(--card-bg)',
          color: 'var(--text-color)',
          borderColor: 'var(--muted-color)',
        }}
        value={selectedClass}
        onChange={e => setSelectedClass(e.target.value)}
      >
        <option value="">-- choose a class --</option>
        {classes.map(c => (
          <option key={c.id} value={c.id}>
            {c.class_name} (
            {c.start_date?.seconds
              ? new Date(c.start_date.seconds * 1000).toLocaleDateString()
              : '–'}
            {' – '}
            {c.end_date?.seconds
              ? new Date(c.end_date.seconds * 1000).toLocaleDateString()
              : '–'}
            )
          </option>
        ))}
      </select>

      <button
        disabled={!selectedClass || deleting}
        onClick={handleDelete}
        className="px-4 py-2 rounded bg-[var(--primary-color)] text-white"
      >
        {deleting ? 'Deleting…' : 'Delete'}
      </button>

      <div
        className="mt-4 max-h-40 overflow-auto p-2 border border-[var(--muted-color)] rounded bg-[var(--card-bg)]"
        style={{
          borderColor: 'var(--muted-color)',
          backgroundColor: 'var(--card-bg)',
        }}
      >
        {logs.map((l, i) => (
          <div key={i} className="text-sm text-[var(--text-color)]">
            {l}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DeleteClassTab;
